import React, { useState, useEffect } from "react";
import {
  GetAllFollowUpTypes,
  fetchProjectData,
  GetLeadLog,
  LeadLgoList,
  fetchLeadData,
  Leads,
  createUserTask,
} from "../Services/APIService";
import Select from "react-select";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import toast, { Toaster } from "react-hot-toast";
import { useLocation } from "react-router-dom";

export interface followupTypeList {
  id: number;
  name: string;
  isActive: boolean;
}

export interface Project {
  projectID: number;
  projectName: string;
}

interface SelectOption {
  value: number;
  label: string;
  projectData: Project;
}

interface SelectLeadOption {
  value: number;
  label: string;
  leadData: Leads;
}

interface FormData {
  reportTo: SelectOption | null;
  note: string;
  followupDate: string;
  reminderDate: string;
  leadData?: SelectLeadOption | null;
}

interface CreateUserTaskRequest {
  leadId: number;
  logId: number;
  projectId: number;
  assignedUser: number;
  followupTypeId: number;
  followupDate: string;
  taskNotes: string;
  reminderDate: string;
  tenantId: number;
  createdBy: number;
}

const CreateUserTask: React.FC = () => {
  const location = useLocation();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<number | null>(null);
  const [followupType, setFollowupType] = useState<followupTypeList[]>([]);
  const [projectData, setprojectData] = useState<Project[]>([]);
  const [projectOption, setProjectOption] = useState<SelectOption[]>([]);
  const [allowCreateTaskLeadOption, setallowCreateTaskLeadOption] = useState<
    SelectLeadOption[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    reportTo: null,
    note: "",
    followupDate: "",
    reminderDate: "",
  });
  const [leadLogData, setLeadLogData] = useState<LeadLgoList[]>([]);

  useEffect(() => {
    const getFetchpMasteData = async () => {
      setIsLoading(true);

      try {
        //fetch followup types
        const fetchAllFolowUpType = await GetAllFollowUpTypes();
        const activeTypes = fetchAllFolowUpType.filter(
          (type: followupTypeList) => type.isActive
        );
        setFollowupType(activeTypes);

        // Set the first active tab as default
        if (activeTypes.length > 0) {
          setActiveTab(activeTypes[0].id);
        }

        //fetch project
        const fetchAllProjectData = await fetchProjectData();
        const options = fetchAllProjectData.map((project) => ({
          value: project.projectID,
          label: project.projectName,
          projectData: project,
        }));
        setProjectOption(options);
        setprojectData(fetchAllProjectData);

        //FetchLeadLogData
        const leadLogs = await GetLeadLog();
        const allowCreateTaskLead = leadLogs.filter(
          (log) => log.isNextActionRequired === true && log.taskId === null
        );
        const leadIds = allowCreateTaskLead.map((log) => log.leadId);
        setLeadLogData(allowCreateTaskLead);

        //get all lead data
        const fetchAllLeadData = await fetchLeadData();

        const matchedLeads = fetchAllLeadData.filter((lead) =>
          leadIds.includes(lead.leadId)
        );

        const dropdownOptions = matchedLeads.map((lead) => ({
          value: lead.leadId,
          label: `${lead.firstName} ${lead.lastName}`,
          leadData: lead,
        }));
        setallowCreateTaskLeadOption(dropdownOptions);
        if (location.state?.leadId) {
          const selectedLead = dropdownOptions.find(
            (option) => option.value === location.state.leadId
          );

          if (selectedLead) {
            setFormData((prev) => ({
              ...prev,
              leadData: selectedLead,
            }));
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getFetchpMasteData();
  }, []);

  const handleTabClick = (tabId: number) => {
    setActiveTab(tabId);
  };

  const handleSingleSelectChange = (selectedOption: SelectOption | null) => {
    setFormData((prev) => ({
      ...prev,
      reportTo: selectedOption,
    }));
  };

  const handleSingleSelectChangeOfLead = (
    SelectLeadOption: SelectLeadOption | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      leadData: SelectLeadOption,
    }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getLocalStorageValue = (key: string): number => {
    const value = localStorage.getItem(key);
    return value ? parseInt(value, 10) : 0;
  };

  const handleSubmit = async () => {
    if (!activeTab || !formData.reportTo) {
      alert("Please select a project and ensure all fields are filled");
      return;
    }

    if (
      !formData.followupDate ||
      !formData.reminderDate ||
      !formData.note.trim()
    ) {
      alert("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      // Get values from localStorage
      const assignedUser =
        getLocalStorageValue("assignedUser") || getLocalStorageValue("UserId");
      const tenantId = getLocalStorageValue("tenantId");
      const createdBy =
        getLocalStorageValue("createdBy") || getLocalStorageValue("UserId");
      const selectedLeadId = formData.leadData?.value || 0;

      const selectedLeadLogData = leadLogData.find(
        (x) => x.leadId === selectedLeadId
      );

      console.log("Selected Lead Log Data:", selectedLeadLogData);

      // Prepare the request data
      const requestData: CreateUserTaskRequest = {
        leadId: formData.leadData?.value || 0,
        logId: selectedLeadLogData?.logId || 0,
        projectId: formData.reportTo.value,
        assignedUser: selectedLeadLogData?.assignedUser || 0,
        followupTypeId: activeTab,
        followupDate: formData.followupDate,
        taskNotes: formData.note,
        reminderDate: formData.reminderDate,
        tenantId: tenantId,
        createdBy: createdBy,
      };

      // Call the API
      const result = await createUserTask(requestData);
      if (result.isCreated) {
        toast.success("Task created successfully!");
      }
      // Reset form after successful submission
      setFormData({
        reportTo: null,
        note: "",
        followupDate: "",
        reminderDate: "",
      });
    } catch (error) {
      console.error("Error submitting task:", error);
      alert("Error creating task. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to format datetime-local input value
  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Set default datetime values (current time for followup, +1 hour for reminder)
  useEffect(() => {
    const now = new Date();
    const reminderTime = new Date(now.getTime() + 60 * 60 * 1000); // +1 hour

    if (!formData.followupDate) {
      setFormData((prev) => ({
        ...prev,
        followupDate: formatDateTimeLocal(now),
      }));
    }
    if (!formData.reminderDate) {
      setFormData((prev) => ({
        ...prev,
        reminderDate: formatDateTimeLocal(reminderTime),
      }));
    }
  }, []);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  return (
    ///dsaabhdsdshsdjhsdnjhsdnds shsdihdjih

    <div className="wrapper">
      <div>
        <Toaster
          position="top-center"
          reverseOrder={false}
          containerStyle={{
            zIndex: 10000,
          }}
        />
      </div>
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0"></div>
            <div className="card">
              <ul
                className="nav nav-tabs cardHeaderHegiht"
                id="myTab"
                role="tablist"
                style={{ borderBottom: "1px solid rgb(222 226 230)" }}
              >
                {followupType.map((type) => (
                  <li key={type.id} className="nav-item" role="presentation">
                    <button
                      className={`nav-link card-title tab-btn cardHeaderHegiht followUpcardHead ${
                        activeTab === type.id ? "active" : ""
                      }`}
                      id={`tab-${type.id}`}
                      onClick={() => handleTabClick(type.id)}
                      type="button"
                      role="tab"
                      aria-controls={`tab-${type.id}-pane`}
                      aria-selected={activeTab === type.id}
                    >
                      {type.name}
                    </button>
                  </li>
                ))}
              </ul>

              <div className="tab-content card-body" id="myTabContent">
                {followupType.map((type) => (
                  <div
                    key={type.id}
                    className={`tab-pane fade ${
                      activeTab === type.id ? "show active" : ""
                    }`}
                    id={`tab-${type.id}-pane`}
                    role="tabpanel"
                    aria-labelledby={`tab-${type.id}`}
                    tabIndex={0}
                  >
                    <div>
                      <div className="row">
                        <div className="col-12 col-lg-6 mb-3">
                          <label className="form-label">
                            Select Project{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <Select
                            options={projectOption}
                            value={formData.reportTo}
                            onChange={handleSingleSelectChange}
                            className="basic-single-select"
                            classNamePrefix="select"
                            placeholder="Select project"
                            isLoading={isLoading}
                            isClearable
                            isSearchable
                          />
                        </div>

                        <div className="col-12 col-lg-6 mb-3">
                          <label className="form-label">
                            Select Lead <span className="text-danger">*</span>
                          </label>
                          <Select
                            options={allowCreateTaskLeadOption}
                            value={formData.leadData}
                            onChange={handleSingleSelectChangeOfLead}
                            className="basic-single-select"
                            classNamePrefix="select"
                            placeholder="Select Lead"
                            isLoading={isLoading}
                            isClearable
                            isSearchable
                          />
                        </div>

                        <div className="col-12 col-lg-6 mb-3">
                          <label htmlFor="followupDate" className="form-label">
                            Followup Date <span className="text-danger">*</span>
                          </label>
                          <input
                            type="datetime-local"
                            id="followupDate"
                            name="followupDate"
                            className="form-control form-control-lg"
                            value={formData.followupDate}
                            onChange={handleInputChange}
                            required
                          />
                        </div>

                        <div className="col-12 col-lg-6 mb-3">
                          <label htmlFor="reminderDate" className="form-label">
                            Reminder Date <span className="text-danger">*</span>
                          </label>
                          <input
                            type="datetime-local"
                            id="reminderDate"
                            name="reminderDate"
                            className="form-control form-control-lg"
                            value={formData.reminderDate}
                            onChange={handleInputChange}
                            required
                          />
                        </div>

                        <div className="col-12 mb-3">
                          <label htmlFor="note" className="form-label">
                            Task Note <span className="text-danger">*</span>
                          </label>
                          <textarea
                            onChange={handleInputChange}
                            id="note"
                            name="note"
                            className="form-control form-control-lg"
                            placeholder="Enter Task Note"
                            value={formData.note}
                            rows={4}
                            required
                          />
                        </div>
                      </div>

                      {/* <h5>Content for {type.name}</h5>
              <p>Followup Type ID: {type.id}</p> */}

                      {/* {formData.reportTo && (
                <div className="mt-3">
                  <p>
                    <strong>Selected Project:</strong> {formData.reportTo.label}
                  </p>
                  <p>
                    <strong>Project ID:</strong> {formData.reportTo.value}
                  </p>
                </div>
              )} */}
                    </div>
                  </div>
                ))}
              </div>

              <div className="card-footer">
                <button
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={!activeTab || !formData.reportTo || isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : `Submit Task`}
                </button>
              </div>
            </div>
          </main>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default CreateUserTask;
